import { Component } from '@theme/component';

/**
 * Clamps a product description to a number of lines and reveals the rest.
 *
 * The toggle is not rendered as "always there and hidden by CSS": it is
 * measured. A description that already fits inside the clamp gets no button at
 * all, because a "read more" that expands nothing is worse than no button.
 *
 * @extends {Component}
 */
class MmProductDescriptionComponent extends Component {
  requiredRefs = ['body'];

  connectedCallback() {
    super.connectedCallback();
    this.#measure();
    this.#align();

    // The clamp is a line count, so it changes with the column width, not just
    // with the content: a description that overflows at 393 can fit at 1440.
    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.refs.body);

    // The thumbnail column is sized by the gallery (a clamp on the viewport plus
    // its own padding), so its width is read, not restated.
    const gallery = this.#gallery();
    if (gallery) {
      this.#alignObserver = new ResizeObserver(() => this.#align());
      this.#alignObserver.observe(gallery);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#alignObserver?.disconnect();
    super.disconnectedCallback?.();
  }

  /** The gallery that sits above this description, in the same media column. */
  #gallery() {
    return this.closest('.product-information__media')?.querySelector('slideshow-component') ?? null;
  }

  /**
   * Lines the description up with the large image rather than with the
   * thumbnails (QA of 26 Sep). Only when the thumbnails form a column to the
   * left of the image; on a phone they sit under it, or there are none, and the
   * description keeps the full width.
   */
  #align() {
    const gallery = this.#gallery();
    const main = gallery?.querySelector(':scope > slideshow-container');
    const thumbs = gallery?.querySelector(':scope > slideshow-controls');
    if (!main || !thumbs || !this.parentElement) return;

    const mainBox = main.getBoundingClientRect();
    const thumbsBox = thumbs.getBoundingClientRect();
    const beside = thumbsBox.width > 0 && thumbsBox.right <= mainBox.left + 1 && thumbsBox.top < mainBox.bottom;
    const inset = beside ? mainBox.left - this.parentElement.getBoundingClientRect().left : 0;

    this.style.setProperty('--mm-description-inset', `${Math.max(0, inset)}px`);
  }

  /** A re-render can swap in another product's description. */
  updatedCallback() {
    this.#collapse();
    this.#measure();
  }

  toggle() {
    if (this.dataset.mmExpanded === 'true') this.#collapse();
    else this.#expand();
  }

  #expand() {
    this.dataset.mmExpanded = 'true';
    this.refs.toggle?.setAttribute('aria-expanded', 'true');
    if (this.refs.toggleLabel) this.refs.toggleLabel.textContent = this.dataset.mmLess ?? '';
  }

  #collapse() {
    this.dataset.mmExpanded = 'false';
    this.refs.toggle?.setAttribute('aria-expanded', 'false');
    if (this.refs.toggleLabel) this.refs.toggleLabel.textContent = this.dataset.mmMore ?? '';
  }

  /**
   * Hides the button unless the clamped box actually cuts something off.
   * Measured while collapsed, otherwise scrollHeight equals clientHeight and
   * every description looks like it fits.
   */
  #measure() {
    const { body, toggle } = this.refs;
    // With the clamp off there is no button at all -- the whole description is
    // printed and there is nothing to reveal.
    if (!toggle || !body) return;

    const wasExpanded = this.dataset.mmExpanded === 'true';
    if (wasExpanded) this.#collapse();

    // A one-pixel slack: sub-pixel line heights make an exact comparison
    // report an overflow on text that visually fits.
    const overflows = body.scrollHeight - body.clientHeight > 1;
    toggle.hidden = !overflows;

    if (wasExpanded && overflows) this.#expand();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {ResizeObserver | undefined} */
  #alignObserver;
}

if (!customElements.get('mm-product-description-component')) {
  customElements.define('mm-product-description-component', MmProductDescriptionComponent);
}
